import React from 'react';

const SaleOrder: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales Orders Management</h1>
        <p className="text-gray-600">
          Create, manage, and track sales orders for customer procurement and inventory management
        </p>
      </div>

      {/* Sales Orders Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sales Orders Overview</h2>
        <p className="text-gray-600 mb-4">
          Sales Orders in DAS provide a comprehensive system for managing procurement processes from
          order creation to delivery tracking. Create official sales documents, track delivery status,
          and seamlessly convert orders to invoices when items are received.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/lists.png"
            alt="Sales Orders List View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Official Sales order creation and management</li>
              <li>Supplier relationship and communication</li>
              <li>Product quantity and pricing control</li>
              <li>Expected delivery date tracking</li>
              <li>Sales order invoice management</li>
              <li>Convert to invoice functionality</li>
              <li>Search and filtering capabilities</li>
              <li>PDF generation and printing</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Status Types</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Draft:</strong> Sales order being prepared</li>
              <li><strong>Confirmed:</strong> Sales order sent to supplier</li>
              <li><strong>Partially invoice:</strong> Some items converted to invoice</li>
              <li><strong>Closed:</strong> Sales order completed</li>
              <li><strong>Cancelled:</strong> Sales order cancelled</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Creating New Sales Orders */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Sale Orders</h2>
        <p className="text-gray-600 mb-4">
          Create detailed sale orders with comprehensive customer and product information.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/new-form.png"
            alt="New Sales Order Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Header Information</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Basic Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Supplier:</strong> Select vendor with search functionality (required)</li>
              <li><strong>Branch:</strong> Organizational branch assignment</li>
              <li><strong>Creation Date:</strong> Sales order creation date</li>
              <li><strong>Expected Delivery Date:</strong> When items are needed</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Additional Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Reference #:</strong> Optional reference number</li>
              <li><strong>Warehouse to Deliver:</strong> Delivery location</li>
              <li><strong>Payment Terms:</strong> Payment conditions</li>
              <li><strong>Currency:</strong> Transaction currency</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Product Details Section</h3>
        <p className="text-gray-600 mb-4">
          Add products to your Sales order with detailed quantity and pricing information:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Product Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Product Details:</strong> Select items from inventory</li>
              <li><strong>Account:</strong> Chart of accounts classification</li>
              <li><strong>Quantity:</strong> Number of units to order</li>
              <li><strong>Rate:</strong> Price per unit</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Additional Options:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Discount:</strong> Apply percentage or fixed discounts</li>
              <li><strong>Tax:</strong> Tax rates and calculations</li>
              <li><strong>Amount:</strong> Total line amount (auto-calculated)</li>
              <li><strong>Description:</strong> Additional product notes</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Warehouse and Tax Settings</h3>
        <p className="text-gray-600 mb-4">
          Configure warehouse and tax settings for accurate inventory and financial management:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Warehouse Selection:</strong> Choose destination warehouse for delivery</li>
          <li><strong>Tax Exclusive:</strong> Option to exclude tax from line amounts</li>
          <li><strong>At Line Item Level:</strong> Apply taxes at individual item level</li>
          <li><strong>Customer Notes:</strong> Special instructions or terms</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Line Item Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Add New Row:</strong> Add additional product lines</li>
              <li><strong>Add Products in Bulk:</strong> Import multiple products</li>
              <li><strong>Remove Items:</strong> Delete specific product lines</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Form Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Save As Draft:</strong> Save without confirming</li>
              <li><strong>Save And Confirm:</strong> Finalize Sales order</li>
              <li><strong>Cancel:</strong> Discard changes and close form</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Inventory Tip:</strong> The system shows past and current quantities for each product,
            helping you make informed ordering decisions based on available stock levels.
          </p>
        </div>
      </div>

      {/* Search and Filtering */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Search and Filtering</h2>
        <p className="text-gray-600 mb-4">
          Efficiently locate and manage sale orders using comprehensive search and filtering options.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/search-filter.png"
            alt="Sale Orders Search Dialog"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Advanced Search Dialog</h3>
        <p className="text-gray-600 mb-4">
          Use the comprehensive search dialog to find specific sale orders:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Identification Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Sales Order #:</strong> Search by PO number</li>
              <li><strong>Reference #:</strong> Find by reference number</li>
              <li><strong>Status:</strong> Filter by order status</li>
              <li><strong>Branch:</strong> Filter by organizational branch</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Date and Entity Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Order Date Range:</strong> Filter by creation dates</li>
              <li><strong>Expected Delivery Date Range:</strong> Filter by delivery dates</li>
              <li><strong>Warehouse:</strong> Filter by delivery warehouse</li>
              <li><strong>Supplier:</strong> Search by supplier with lookup</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
        <p className="text-gray-600 mb-4">
          Use the search icon (🔍) in the top-right corner for instant sale order filtering:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Real-time Search:</strong> Filter sale orders as you type</li>
          <li><strong>Multi-field Search:</strong> Searches across PO numbers, suppliers, and references</li>
          <li><strong>Quick Access:</strong> No separate forms needed for basic searches</li>
          <li><strong>Immediate Results:</strong> Instant table filtering</li>
        </ul>
      </div>

      {/* Editing Sale Orders */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Editing Sale Orders</h2>
        <p className="text-gray-600 mb-4">
          Modify existing sale orders to update quantities, delivery dates, or supplier information.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/edit-form.png"
            alt="Edit Sales Order Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Editable Fields</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Header Updates:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Change supplier and delivery address</li>
              <li>Update branch and reference information</li>
              <li>Modify delivery dates and payment terms</li>
              <li>Adjust currency and warehouse settings</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Product Line Updates:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Modify quantities and rates</li>
              <li>Add or remove product lines</li>
              <li>Update discounts and tax settings</li>
              <li>Change account classifications</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Inventory Tracking</h3>
        <p className="text-gray-600 mb-4">
          The edit form displays current inventory information to help with quantity decisions:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Past Quantity:</strong> Previously ordered amounts for reference</li>
          <li><strong>Current Quantity:</strong> Available stock levels</li>
          <li><strong>Account Selection:</strong> Proper expense or asset account assignment</li>
          <li><strong>Remove Options:</strong> Delete individual line items as needed</li>
        </ul>
      </div>

      {/* Sales Order Details and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sales Order Details and Management</h2>
        <p className="text-gray-600 mb-4">
          View comprehensive Sales order information and access management actions from the detail view.
        </p>

        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/detail.png"
            alt="Sales Order Detail View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Detail View Features</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Order Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Complete sale order header details</li>
              <li>Supplier information and contact details</li>
              <li>Delivery dates and payment terms</li>
              <li>Product details with quantities and amounts</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Management Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Edit sale order information</li>
              <li>PDF generation and printing</li>
              <li>Attach files and documentation</li>
              <li>Comments and history tracking</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Menu Options</h3>
        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/draft-status.png"
            alt="Sales Order More Actions"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>
        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/confirm-status.png"
            alt="Sales Order More Actions"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>
        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/close-status.png"
            alt="Sales Order More Actions"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Primary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Clone:</strong> Create similar sale orders</li>
              <li><strong>Convert to invoice:</strong> Generate invoice for received items</li>
              <li><strong>Confirm sale Order:</strong> Finalize draft orders</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Management Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Partially Closed:</strong> Close completed portions</li>
              <li><strong>Cancel Sales Order:</strong> Cancel unwanted orders</li>
              <li><strong>Delete:</strong> Remove sale orders</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Invoice Integration</h3>
        <div className="mb-6">
          <img
            src="/images/sales/sales-orders/convert-invoice.png"
            alt="Sales Order invoice Tab"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>
        <p className="text-gray-600 mb-4">
          Track the conversion of sale orders to invoice with detailed invoice information:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>invoice Numbers:</strong> Reference to generated invoice</li>
          <li><strong>invoice Dates:</strong> When invoice were created</li>
          <li><strong>invoice Status:</strong> Confirmed or pending status</li>
          <li><strong>Due Dates:</strong> Payment due dates for invoices</li>
          <li><strong>Amounts:</strong> Invoice amounts and remaining balances</li>
          <li><strong>PO Status Tracking:</strong> Shows "Partially invoice" status</li>
        </ul>
      </div>

      {/* PDF Generation and Printing */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">PDF Generation and Document Management</h2>
        <p className="text-gray-600 mb-4">
          Generate professional purchase order documents for supplier communication and record keeping.
        </p>

        {/* <div className="mb-6">
          <img
            src="/images/purchase/purchase-orders/pdf.png"
            alt="Purchase Order PDF View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div> */}

        <h3 className="text-lg font-semibold text-gray-800 mb-3">PDF Features</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Document Elements:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Professional branded header with company logo</li>
              <li>Complete sale order details and totals</li>
              <li>Supplier information and contact details</li>
              <li>Product table with descriptions and amounts</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Business Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Sale order number and date</li>
              <li>Delivery date and payment terms</li>
              <li>Currency and tax information</li>
              <li>Terms and conditions</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Document Actions</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>PDF/Print Button:</strong> Generate and download PDF documents</li>
          <li><strong>Email Integration:</strong> Send sale orders directly to suppliers</li>
          <li><strong>Template Customization:</strong> Modify document layout and branding</li>
          <li><strong>Attachment Support:</strong> Add supporting documents to sale orders</li>
        </ul>
      </div>

      {/* Status Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sale Order Status Management</h2>
        <p className="text-gray-600 mb-4">
          Track and manage sale orders through their complete lifecycle from creation to completion.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Status Types and Workflow</h3>
        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Draft Status</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Sales order being prepared and reviewed</li>
              <li>• Can be edited and modified freely</li>
              <li>• Not yet sent to supplier</li>
              <li>• Shown in orange status indicator</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Confirmed Status</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Sales order finalized and sent to supplier</li>
              <li>• Official procurement document</li>
              <li>• Ready for supplier processing</li>
              <li>• Shown in blue status indicator</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Partially Invoice Status</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Some items received and converted to invoice</li>
              <li>• Remaining items still pending delivery</li>
              <li>• Partial completion of sale order</li>
              <li>• Shown in blue status with progress indication</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-700 mb-2">Closed Status</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Sales order completed successfully</li>
              <li>• All items received and processed</li>
              <li>• Final status for completed orders</li>
              <li>• Shown in green status indicator</li>
            </ul>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            <strong>Workflow Tip:</strong> Sales orders provide a structured procurement process,
            ensuring proper authorization, supplier communication, and accurate financial tracking.
            Use status management to monitor order progress and maintain control over procurement activities.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SaleOrder;
